import { useEffect, useState } from "react";
import axios from "utils/axios";
import Select from "react-select";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import {
  FaEdit,
  FaTrash,
  FaDownload,
  FaFolder,
  FaArrowLeft,
} from "react-icons/fa";
import { showToast } from "components/ui/toast";
import { useGetSuburbQuery } from "apis/suburb";

const AddAlert = () => {
  const [authSchedules, setAuthSchedules] = useState([]);
  const [areaTriggers, setAreaTriggers] = useState([]);
  const [propertyTriggers, setPropertyTriggers] = useState([]);
  const [suburbs, setSuburbs] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [triggerType, setTriggerType] = useState(null);
  const [loading, setLoading] = useState(false);

  // Toggle between PDF and Image+Message mode
  const [uploadMode, setUploadMode] = useState("pdf"); // "image" or "pdf"

  // File states - unified for both image and PDF
  const [filePreview, setFilePreview] = useState(null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [message, setMessage] = useState("");

  // Validation states
  const [showValidation, setShowValidation] = useState(false);

  // Area Postcards data
  const [areaPostcards, setAreaPostcards] = useState([]);
  const [loadingPostcards, setLoadingPostcards] = useState(false);

  // Delete modal states
  const [deleteLoading, setDeleteLoading] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postcardToDelete, setPostcardToDelete] = useState(null);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [postcardToEdit, setPostcardToEdit] = useState(null);
  const [editFilePreview, setEditFilePreview] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editFileName, setEditFileName] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editUploadMode, setEditUploadMode] = useState("pdf");

  // Folder navigation states
  const [viewMode, setViewMode] = useState("folders"); // "folders" or "postcards"
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthFolders, setMonthFolders] = useState([]);

  const { data: suburbData, isPending: suburbsLoading } = useGetSuburbQuery();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [schedulesRes, triggersRes] = await Promise.all([
          axios.get("/admin/authSchedule", {
            params: { select: "address,propertyId" },
          }),
          axios.get("/admin/communityTriggers"),
        ]);

        const scheduleOptions = schedulesRes.data.data.map((item) => ({
          label: item.address,
          value: item.propertyId,
        }));

        // Separate triggers by type
        const areaTriggersData = triggersRes.data.data
          .filter((t) => t.type?.name === "Area")
          .map((t) => ({
            label: t.trigger,
            value: t,
          }));

        const propertyTriggersData = triggersRes.data.data
          .filter((t) => t.type?.name === "Property")
          .map((t) => ({
            label: t.trigger,
            value: t,
          }));

        setAuthSchedules(scheduleOptions);
        setAreaTriggers(areaTriggersData);
        setPropertyTriggers(propertyTriggersData);
      } catch (err) {
        console.error("Error loading options:", err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (Array.isArray(suburbData)) {
      setSuburbs(
        suburbData.map((item) => ({
          label: item.suburb,
          value: item.suburb,
        }))
      );
    }
  }, [suburbData]);

  // Utility function to format month-year
  const formatMonthYear = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  // Utility function to get month-year key
  const getMonthYearKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  // Group postcards by month
  const groupPostcardsByMonth = (postcards) => {
    const grouped = postcards.reduce((acc, postcard) => {
      const monthKey = getMonthYearKey(postcard.createdAt);
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push(postcard);
      return acc;
    }, {});

    // Convert to array and sort by date (newest first)
    const folders = Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .map((monthKey) => ({
        key: monthKey,
        label: formatMonthYear(grouped[monthKey][0].createdAt),
        count: grouped[monthKey].length,
        postcards: grouped[monthKey],
      }));

    return folders;
  };

  // Fetch Area Postcards data
  const fetchAreaPostcards = async () => {
    setLoadingPostcards(true);
    try {
      const response = await axios.get("/admin/communityAlerts");
      if (response.data.success) {
        const postcards = response.data.data.filter(
          (item) =>
            item.message === "Area Postcards" ||
            item.triggerId?.trigger === "AREA_POSTCARDS"
        );
        setAreaPostcards(postcards);

        // Group postcards by month for folder view
        const folders = groupPostcardsByMonth(postcards);
        setMonthFolders(folders);
      }
    } catch (error) {
      console.error("Failed to fetch area postcards:", error);
    } finally {
      setLoadingPostcards(false);
    }
  };

  useEffect(() => {
    fetchAreaPostcards();
  }, []);

  const triggerTypeOptions = [
    { label: "Area", value: "Area" },
    { label: "Property", value: "Property" },
  ];

  const handleTriggerTypeChange = (selectedType) => {
    setTriggerType(selectedType);
    setSelectedTrigger(null);
    setSelectedSchedule(null);
    setSelectedSuburb(null);
    resetUploadStates();
    setShowValidation(false);
  };

  const resetUploadStates = () => {
    setFilePreview(null);
    setFile(null);
    setFileName("");
    setMessage("");
    setUploadMode("pdf");
  };

  const handleTriggerChange = (selectedTrigger) => {
    setSelectedTrigger(selectedTrigger);
    resetUploadStates();
    setShowValidation(false);
  };

  const handleUploadModeChange = (mode) => {
    setUploadMode(mode);
    // Reset states when switching modes
    setFilePreview(null);
    setFile(null);
    setFileName("");
    setMessage("");
    setShowValidation(false);
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validate file type based on upload mode
    if (uploadMode === "image" && !selectedFile.type.startsWith("image/")) {
      showToast("error", "Please select a valid image file");
      return;
    }

    if (uploadMode === "pdf" && selectedFile.type !== "application/pdf") {
      showToast("error", "Please select a valid PDF file");
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);

    // Create preview for images
    if (uploadMode === "image" && selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  const getCurrentTriggers = () => {
    if (!triggerType) return [];
    return triggerType.value === "Area" ? areaTriggers : propertyTriggers;
  };

  const getValidationErrors = () => {
    const errors = {};

    if (!triggerType) {
      errors.triggerType = "Please select a trigger type";
    }

    if (!selectedTrigger) {
      errors.trigger = "Please select a trigger";
    }

    if (triggerType?.value === "Property" && !selectedSchedule) {
      errors.schedule = "Please select a property address";
    }

    if (triggerType?.value === "Area" && !selectedSuburb) {
      errors.suburb = "Please select a suburb";
    }

    if (selectedTrigger?.value?.trigger === "AREA_POSTCARDS") {
      if (!file) {
        errors.file = `Please upload ${
          uploadMode === "image" ? "an image" : "a PDF"
        } file`;
      }
      if (uploadMode === "image" && !message.trim()) {
        errors.message = "Please enter a message";
      }
    }

    return errors;
  };

  const addAlert = async () => {
    setShowValidation(true);
    const errors = getValidationErrors();

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        triggerName: selectedTrigger.value.trigger,
        uploadMode: uploadMode,
      };

      if (triggerType.value === "Property") {
        payload.propertyId = selectedSchedule.value;
      } else if (triggerType.value === "Area") {
        payload.suburb = selectedSuburb.value;
      }

      if (selectedTrigger?.value?.trigger === "AREA_POSTCARDS") {
        const formData = new FormData();
        Object.keys(payload).forEach((key) => {
          formData.append(key, payload[key]);
        });

        formData.append("file", file);

        if (uploadMode === "image") {
          formData.append("message", message);
        }

        await axios.post("/admin/communityAlerts", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await axios.post("/admin/communityAlerts", payload);
      }

      showToast("success", "Alert added successfully");

      // Reset form
      resetUploadStates();
      setSelectedSchedule(null);
      setSelectedSuburb(null);
      setSelectedTrigger(null);
      setTriggerType(null);
      setShowValidation(false);

      // Refresh the area postcards list
      fetchAreaPostcards();

      // Reset to folder view after adding
      setViewMode("folders");
      setSelectedMonth(null);
    } catch (err) {
      console.error("Error adding alert:", err);
      showToast("error", "Error adding alert");
    } finally {
      setLoading(false);
    }
  };

  // Folder navigation functions
  const handleFolderClick = (folder) => {
    setSelectedMonth(folder);
    setViewMode("postcards");
  };

  const handleBackToFolders = () => {
    setViewMode("folders");
    setSelectedMonth(null);
  };

  const getCurrentPostcards = () => {
    if (viewMode === "folders") return [];
    return selectedMonth ? selectedMonth.postcards : areaPostcards;
  };

  const handleEdit = (postcard) => {
    setPostcardToEdit(postcard);
    setEditMessage(postcard.vessel?.[0]?.message || "");

    // Determine if this postcard was created with PDF or image+message
    const hasPdf =
      postcard.vessel?.[0]?.url && postcard.vessel[0].url.endsWith(".pdf");
    setEditUploadMode(hasPdf ? "pdf" : "image");

    if (hasPdf) {
      setEditFileName("Current PDF");
      setEditFilePreview(null);
    } else {
      // Show image URL directly
      if (postcard.propertyDetails?.image) {
        setEditFilePreview(postcard.propertyDetails.image);
      } else {
        setEditFilePreview(null);
      }
      setEditFileName("Current Image");
    }

    setEditFile(null);
    setShowEditModal(true);
  };

  const handleEditUploadModeChange = (mode) => {
    setEditUploadMode(mode);
    // Reset states when switching modes
    setEditFilePreview(null);
    setEditFile(null);
    setEditFileName("");
  };

  const handleEditFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validate file type based on upload mode
    if (editUploadMode === "image" && !selectedFile.type.startsWith("image/")) {
      showToast("error", "Please select a valid image file");
      return;
    }

    if (editUploadMode === "pdf" && selectedFile.type !== "application/pdf") {
      showToast("error", "Please select a valid PDF file");
      return;
    }

    setEditFile(selectedFile);
    setEditFileName(selectedFile.name);

    // Create preview for images
    if (editUploadMode === "image" && selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditFilePreview(e.target.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setEditFilePreview(null);
    }
  };

  const handleEditSave = async () => {
    if (!postcardToEdit) return;

    // Check if anything changed
    const fileChanged = editFile !== null;
    const messageChanged =
      editMessage !== (postcardToEdit.vessel?.[0]?.message || "");

    if (!fileChanged && !messageChanged) {
      showToast("info", "No changes detected");
      return;
    }

    setEditLoading(true);
    try {
      const formData = new FormData();
      formData.append("uploadMode", editUploadMode);

      if (fileChanged) {
        formData.append("file", editFile);
      }

      if (editUploadMode === "image" && messageChanged) {
        formData.append("message", editMessage);
      }

      const response = await axios.put(
        `/admin/communityAlerts/${postcardToEdit._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        showToast("success", "Alert updated successfully");
        setShowEditModal(false);
        setPostcardToEdit(null);
        setEditFilePreview(null);
        setEditFile(null);
        setEditFileName("");
        setEditMessage("");

        // Refresh the area postcards list
        fetchAreaPostcards();
      }
    } catch (error) {
      console.error("Error updating alert:", error);
      showToast("error", "Error updating alert");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (postcard) => {
    setPostcardToDelete(postcard);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postcardToDelete) return;

    setDeleteLoading((prev) => ({ ...prev, [postcardToDelete._id]: true }));
    try {
      const res = await axios.delete(
        `/admin/communityAlerts/${postcardToDelete._id}`
      );
      if (res.data.success) {
        showToast("success", "Alert deleted successfully");

        // Refresh the area postcards list
        fetchAreaPostcards();
      }
    } catch (error) {
      console.error("Error deleting alert:", error);
      showToast("error", "Error deleting alert");
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [postcardToDelete._id]: false }));
      setShowDeleteModal(false);
      setPostcardToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPostcardToDelete(null);
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setPostcardToEdit(null);
    setEditFilePreview(null);
    setEditFile(null);
    setEditFileName("");
    setEditMessage("");
  };

  const handleDownload = (postcard) => {
    if (
      postcard.vessel &&
      postcard.vessel.length > 0 &&
      postcard.vessel[0].url
    ) {
      window.open(postcard.vessel[0].url, "_blank");
    } else {
      showToast("warning", "No download URL available for this postcard");
    }
  };

  const isAddButtonDisabled = () => {
    return loading;
  };

  const isEditSaveEnabled = () => {
    if (!postcardToEdit) return false;

    const fileChanged = editFile !== null;
    const messageChanged =
      editMessage !== (postcardToEdit.vessel?.[0]?.message || "");

    return fileChanged || messageChanged;
  };

  const renderUploadToggle = () => (
    <div className="flex items-center gap-4 mb-4">
      <span className="font-medium">Upload Mode:</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleUploadModeChange("image")}
          className={`text-xs px-2 py-1 rounded border ${
            uploadMode === "image"
              ? "bg-black text-white border-black"
              : "bg-white text-black border-mediumgray"
          }`}
        >
          Image + Message
        </button>
        <button
          type="button"
          onClick={() => handleUploadModeChange("pdf")}
          className={`text-xs px-2 py-1 rounded border ${
            uploadMode === "pdf"
              ? "bg-black text-white border-black"
              : "bg-white text-black border-mediumgray"
          }`}
        >
          PDF Upload
        </button>
      </div>
    </div>
  );

  const renderEditUploadToggle = () => (
    <div className="flex items-center gap-4 mb-4">
      <span className="font-medium">Upload Mode:</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleEditUploadModeChange("image")}
          className={`text-xs px-2 py-1 rounded border ${
            editUploadMode === "image"
              ? "bg-black text-white border-black"
              : "bg-white text-black border-mediumgray"
          }`}
        >
          Image + Message
        </button>
        <button
          type="button"
          onClick={() => handleEditUploadModeChange("pdf")}
          className={`text-xs px-2 py-1 rounded border ${
            editUploadMode === "pdf"
              ? "bg-black text-white border-black"
              : "bg-white text-black border-mediumgray"
          }`}
        >
          PDF Upload
        </button>
      </div>
    </div>
  );

  const renderFileUpload = () => {
    const isImage = uploadMode === "image";
    const acceptType = isImage ? "image/*" : ".pdf";
    const fileType = isImage ? "image" : "PDF";

    return (
      <div>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center ${
            showValidation && getValidationErrors().file
              ? "border-red-500"
              : "border-mediumgray"
          }`}
        >
          {file ? (
            <div>
              {isImage && filePreview ? (
                <img
                  src={filePreview}
                  alt="Uploaded file"
                  className="max-w-full max-h-64 mx-auto rounded mb-4"
                />
              ) : (
                <div className="flex items-center justify-center mb-4">
                  <svg
                    className="w-12 h-12"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
              <p className="text-sm font-medium mb-4">{fileName}</p>
              <label className="black-button cursor-pointer">
                Change {fileType}
                <input
                  type="file"
                  accept={acceptType}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          ) : (
            <div>
              <svg
                className="mx-auto h-12 w-12 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isImage ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                )}
              </svg>
              <label className="black-button cursor-pointer">
                Upload {fileType}
                <input
                  type="file"
                  accept={acceptType}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </div>
        {showValidation && getValidationErrors().file && (
          <p className="form-error-message">{getValidationErrors().file}</p>
        )}
      </div>
    );
  };

  const renderEditFileUpload = () => {
    const isImage = editUploadMode === "image";
    const acceptType = isImage ? "image/*" : ".pdf";
    const fileType = isImage ? "image" : "PDF";

    return (
      <div>
        <label className="form-label">File</label>
        <div className="border-2 border-dashed border-mediumgray rounded-lg p-4 text-center">
          {editFile || editFileName ? (
            <div>
              {isImage && editFilePreview ? (
                <img
                  src={editFilePreview}
                  alt="File preview"
                  className="max-w-full max-h-64 mx-auto rounded mb-4"
                />
              ) : (
                <div className="flex items-center justify-center mb-4">
                  <svg
                    className="w-12 h-12"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
              <p className="text-sm font-medium mb-4">{editFileName}</p>
              <label className="black-button cursor-pointer">
                Change {fileType}
                <input
                  type="file"
                  accept={acceptType}
                  onChange={handleEditFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          ) : (
            <div>
              <svg
                className="mx-auto h-12 w-12 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <label className="black-button cursor-pointer">
                Upload {fileType}
                <input
                  type="file"
                  accept={acceptType}
                  onChange={handleEditFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMonthFolders = () => {
    if (loadingPostcards) {
      return <p>Loading area postcards...</p>;
    }

    if (monthFolders.length === 0) {
      return <p className="text-darkgray">No area postcards found.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {monthFolders.map((folder) => (
          <div
            key={folder.key}
            className="border border-mediumgray rounded-lg p-4 bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleFolderClick(folder)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-lightgray rounded-lg">
                <FaFolder className="text-2xl text-darkergray" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-lg">{folder.label}</p>
                <p className="text-sm text-darkergray">
                  {folder.count} postcard{folder.count !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPostcardsList = () => {
    const postcards = getCurrentPostcards();

    if (loadingPostcards) {
      return <p>Loading postcards...</p>;
    }

    if (postcards.length === 0) {
      return (
        <p className="text-darkgray">No postcards found in this folder.</p>
      );
    }

    return (
      <div className="space-y-4">
        {postcards.map((postcard) => (
          <div
            key={postcard._id}
            className="border border-mediumgray rounded-lg p-4 bg-white shadow-sm"
          >
            <div className="flex flex-col gap-4">
              <div>
                <p className="font-medium text-lg">
                  {postcard.propertyDetails?.suburb}
                </p>
                {postcard.vessel && postcard.vessel.length > 0 && (
                  <>
                    <p className="mt-2 text-sm line-clamp-3">
                      {postcard.vessel[0].message}
                    </p>
                    <p className="text-sm mt-1">
                      Quantity: {postcard.vessel[0].quantity || "N/A"}
                    </p>
                    <p className="text-sm mt-1">
                      Type: {postcard?.propertyDetails?.uploadMode}
                    </p>
                  </>
                )}
                <p className="text-xs mt-2">
                  Created: {new Date(postcard.createdAt).toLocaleDateString()}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <Button
                  onClick={() => handleEdit(postcard)}
                  className="gray-button flex items-center justify-center gap-2"
                  title="Edit"
                >
                  <FaEdit className="flex-shrink-0" />
                  <span>Edit</span>
                </Button>

                <Button
                  onClick={() => handleDeleteClick(postcard)}
                  className="gray-button flex items-center justify-center gap-2"
                  title="Delete"
                  loading={deleteLoading[postcard._id]}
                >
                  <FaTrash className="text-red-500 flex-shrink-0" />
                  <span>Delete</span>
                </Button>

                <Button
                  onClick={() => handleDownload(postcard)}
                  className="black-button flex items-center justify-center gap-2"
                  title="Download"
                >
                  <FaDownload className="flex-shrink-0" />
                  <span>Download</span>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container space-y-4">
      {/* Trigger Type Selection */}
      <div>
        <Select
          className={`w-full ${
            showValidation && getValidationErrors().triggerType
              ? "border-red-500"
              : ""
          }`}
          placeholder="Select Trigger Type"
          options={triggerTypeOptions}
          value={triggerType}
          onChange={handleTriggerTypeChange}
        />
        {showValidation && getValidationErrors().triggerType && (
          <p className="form-error-message">
            {getValidationErrors().triggerType}
          </p>
        )}
      </div>

      {/* Trigger Selection */}
      {triggerType && (
        <div>
          <Select
            className={`w-full ${
              showValidation && getValidationErrors().trigger
                ? "border-red-500"
                : ""
            }`}
            placeholder="Select Trigger"
            options={getCurrentTriggers()}
            value={selectedTrigger}
            onChange={handleTriggerChange}
          />
          {showValidation && getValidationErrors().trigger && (
            <p className="form-error-message">
              {getValidationErrors().trigger}
            </p>
          )}
        </div>
      )}

      {/* Upload Mode Toggle for AREA_POSTCARDS */}
      {selectedTrigger?.value?.trigger === "AREA_POSTCARDS" &&
        renderUploadToggle()}

      {/* File Upload for AREA_POSTCARDS */}
      {selectedTrigger?.value?.trigger === "AREA_POSTCARDS" &&
        renderFileUpload()}

      {/* Message Input for Image Mode */}
      {selectedTrigger?.value?.trigger === "AREA_POSTCARDS" &&
        uploadMode === "image" && (
          <div>
            <textarea
              className={`form-input w-full border ${
                showValidation && getValidationErrors().message
                  ? "border-red-500"
                  : "border-mediumgray"
              }`}
              placeholder="Enter postcard message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
            />
            {showValidation && getValidationErrors().message && (
              <p className="form-error-message">
                {getValidationErrors().message}
              </p>
            )}
          </div>
        )}

      {/* Conditional dropdowns based on trigger type */}
      {triggerType?.value === "Property" && (
        <div>
          <Select
            className={`w-full ${
              showValidation && getValidationErrors().schedule
                ? "border-red-500"
                : ""
            }`}
            placeholder="Select Property Address"
            options={authSchedules}
            value={selectedSchedule}
            onChange={setSelectedSchedule}
          />
          {showValidation && getValidationErrors().schedule && (
            <p className="form-error-message">
              {getValidationErrors().schedule}
            </p>
          )}
        </div>
      )}

      {triggerType?.value === "Area" && (
        <div>
          <Select
            className={`w-full ${
              showValidation && getValidationErrors().suburb
                ? "border-red-500"
                : ""
            }`}
            placeholder="Select Suburb"
            options={suburbs}
            value={selectedSuburb}
            onChange={setSelectedSuburb}
          />
          {showValidation && getValidationErrors().suburb && (
            <p className="form-error-message">{getValidationErrors().suburb}</p>
          )}
        </div>
      )}

      <Button
        className="black-button"
        loading={loading}
        disabled={isAddButtonDisabled()}
        onClick={addAlert}
      >
        Add Alert
      </Button>

      {/* Area Postcards Display Section */}
      <div className="mt-8">
        <h4 className="mb-2">Area Postcards</h4>

        {/* Show current folder name when viewing postcards */}
        {viewMode === "postcards" && selectedMonth && (
          <div className="mb-4 p-3 bg-lightergray rounded-lg">
            <Button
              onClick={handleBackToFolders}
              className="outline-button flex items-center gap-2"
            >
              <FaArrowLeft />
              <span>Back</span>
            </Button>

            <p className="text-sm mt-4">Viewing postcards from:</p>
            <p className="font-medium text-lg">{selectedMonth.label}</p>
            {/* Back button when viewing postcards */}
          </div>
        )}

        {/* Render either folders or postcards based on current view */}
        {viewMode === "folders" ? renderMonthFolders() : renderPostcardsList()}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Confirm Delete"
      >
        <div className="space-y-4">
          <p>
            Are you sure you want to delete this alert? This action cannot be
            undone.
          </p>
          <div className="flex gap-2 justify-end">
            <Button onClick={handleDeleteCancel} className="gray-button">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="black-button"
              loading={deleteLoading[postcardToDelete?._id]}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={handleEditCancel}
        className="max-w-2xl"
        title="Edit Alert"
      >
        <div className="space-y-4">
          {/* Upload Mode Toggle */}
          {renderEditUploadToggle()}

          {/* File Upload */}
          {renderEditFileUpload()}

          {/* Message Input for Image Mode */}
          {editUploadMode === "image" && (
            <div>
              <label className="form-label">Message</label>
              <textarea
                className="form-input w-full border border-mediumgray"
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                rows={8}
                placeholder="Enter postcard message"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <Button onClick={handleEditCancel} className="gray-button">
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              className="black-button"
              loading={editLoading}
              disabled={!isEditSaveEnabled()}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddAlert;
